import React from 'react';
import PageHero from '../components/PageHero';
import Status from '../components/Status';
import SourceLink from '../components/SourceLink';
import { SITE, SOURCES } from '../content/site';

const Sentinel: React.FC = () => (
  <div>
    <PageHero
      title="Grid Data Sentinel"
      lead="A Python package for detecting and correcting anomalies in load telemetry. It is built to keep genuine extreme readings, repair corrupted ones, and record every correction, so that the data behind a forecast can be trusted and audited."
    >
      <div className="flex flex-wrap items-center gap-3">
        <Status tone="live">v0.5.0 released · October 2026</Status>
        <Status tone="neutral">Apache-2.0</Status>
        <Status tone="neutral">Python</Status>
      </div>
    </PageHero>

    <section className="bg-white section-padding">
      <div className="container-width">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-3xl font-poppins font-bold text-dark-gray mb-6">Methods</h2>
            <div className="space-y-6">
              <Method tag="TEDA" color="bg-primary-red" title="Recursive typicality and eccentricity analysis">
                A streaming, distribution-free way to score how unusual each new reading is relative to what the
                series has done so far. Suited to real-time telemetry; developed in the maintainer's M.Sc. research on
                outlier detection in energy demand curves.
              </Method>
              <Method tag="SAE" color="bg-light-blue" title="Sparse autoencoders for detection and correction">
                Learn the normal shape of demand curves and reconstruct corrupted points. Published and tested on real
                operator data in <SourceLink source={SOURCES.energies2024} />.
              </Method>
              <Method tag="RULES" color="bg-dark-gray" title="Rules, a calendar-aware profile and an audited repair">
                A stuck-value rule catches frozen telemetry that no statistical detector sees. Since v0.2 every alarm
                is confirmed against an expected profile built from the day type, the last two weeks and the same weeks
                of the previous year, so that a genuine cold-snap peak is never written off as bad data. Detection never
                modifies the series: it returns the original values with a flag and the reason for each flag. Repair
                is a separate, explicit step that uses equivalent days and is logged reading by reading; gaps longer
                than 48 hours are left open rather than invented.
              </Method>
            </div>
          </div>
          <div className="bg-gray-50 rounded-2xl p-8">
            <h3 className="text-2xl font-poppins font-semibold text-dark-gray mb-6">Benchmark v0.5.0</h3>
            <p className="text-gray-700 text-sm mb-4">
              Ten large balancing authorities, hourly demand for 2023 and 2024 from EIA-930, 0.5% of readings
              corrupted with labelled spikes, dips, zeros, frozen runs and unit errors. Means over 20 series,
              tolerance of one reading. Source: <SourceLink source={SOURCES.sentinelBenchmark} />.
            </p>
            <ul className="space-y-3 text-gray-700 text-sm">
              <li>• Composite v0.5: adjusted precision 0.994, recall 0.986; dips caught 95% of the time against 71% for v0.4, spikes 100% against 95%.</li>
              <li>• The gain comes from keeping condemned readings out of the statistics the next reading is judged against, and from keeping extremes that build over hours: on the public series, v0.4 flagged genuine heat-wave hours that v0.5 keeps (<SourceLink source={SOURCES.sentinelV05Cases} />).</li>
              <li>• Composite v0.2: adjusted precision 0.96, recall 0.98; composite v0.1: 0.93 and 0.92. The profile confirmation was the difference then.</li>
              <li>• Sparse autoencoder: recall 0.93 but blind to frozen values, which the rule catches.</li>
              <li>• Reference detectors, including the modified z-score and the 15% rule of utility practice, sit well below.</li>
              <li>• Partial loss of telemetry (a feeder dropping out of the total for hours) is missed by every composite. The optional feeder-loss detector of v0.5 finds about 60% to 74% of injected losses of 5% to 30% of the load; it stays optional because it also flags real days that look like a loss. Source: <SourceLink source={SOURCES.sentinelFeederLoss} />.</li>
              <li>• The public series already contain about 11,500 zero hours, 9,900 frozen hours and 2,600 unit-error hours; a gallery of real cases is in <SourceLink source={SOURCES.sentinelRealCases} />.</li>
            </ul>
            <h3 className="text-2xl font-poppins font-semibold text-dark-gray mt-8 mb-4">Winter storms</h3>
            <p className="text-gray-700 text-sm">
              Since v0.3 a reading flagged above the expected profile stays a fault only if at least two of three
              cross-checks fail to confirm it as genuine: the profile itself, the load of the balancing authority's
              interchange neighbours, and the local temperature. On the 72 hours around each authority's peak in
              Winter Storms Uri, Elliott, Gerri and Heather, and the January 2025 Arctic events, the peak reading
              survives the v0.3 composite in 95%, 95%, 100% and 100% of the authorities, against 95%, 88%, 91% and
              100% for v0.2 and 86%, 58%, 77% and 88% for the modified z-score, while faults injected into the same
              windows are still caught (recall 0.90 to 1.00). The v0.5 composite keeps the same peaks, flags half as many
              readings of those windows and catches more of the injected faults (recall 0.92 to 1.00). Source: <SourceLink source={SOURCES.sentinelStorms} />.
            </p>
            <h3 className="text-2xl font-poppins font-semibold text-dark-gray mt-8 mb-4">Roadmap</h3>
            <ul className="space-y-2 text-gray-700 text-sm">
              <li><strong>v0.1</strong> · September 2026 · TEDA + autoencoder + rules, batch mode, EIA-930 benchmark. Released.</li>
              <li><strong>v0.2</strong> · September 2026 · calendar-aware profile confirmation, interval table, equivalent-day repair, real-case gallery. Released, DOI 10.5281/zenodo.22726014.</li>
              <li><strong>v0.3</strong> · September 2026 · temperature regime, neighbour and weather cross-checks that keep genuine extremes, evaluation on four winter storms. Released, DOI 10.5281/zenodo.22846631.</li>
              <li><strong>v0.4</strong> · October 2026 · one estimator contract for every detector, numpy-only core, the whole composite in streaming with a serialisable state, checks for spikes, forecast faults and hour-label misalignment in published series. Released, DOI 10.5281/zenodo.23160968.</li>
              <li><strong>v0.5</strong> · October 2026 · faults kept out of the statistics, extremes that build over hours kept, lone jumps no longer kept, optional feeder-loss detector. Released, DOI 10.5281/zenodo.23337413.</li>
              <li><strong>v1.0</strong> · December 2026 · audit trail, documentation, examples on public data.</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section className="gradient-bg text-white section-padding">
      <div className="container-width text-center">
        <h2 className="text-3xl font-poppins font-bold mb-4">Install, cite, contribute</h2>
        <p className="text-gray-300 mb-4 max-w-2xl mx-auto">
          Install from the repository. Each release is archived on Zenodo: v0.5.0 is DOI 10.5281/zenodo.23337413, v0.4.0 is DOI 10.5281/zenodo.23160968,
          v0.3.0 is DOI 10.5281/zenodo.22846631, v0.2.0 is DOI 10.5281/zenodo.22726014 (concept DOI for all versions 10.5281/zenodo.22726013); the citation
          record is in CITATION.cff.
        </p>
        <pre className="inline-block text-left text-sm bg-black/30 rounded-lg px-4 py-3 mb-8 overflow-x-auto max-w-full">
          pip install git+https://github.com/cardinalgrid/grid-data-sentinel
        </pre>
        <div className="flex flex-wrap justify-center gap-4">
          <a
            href={`${SITE.github}/grid-data-sentinel`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
          >
            Repository on GitHub
          </a>
        </div>
      </div>
    </section>
  </div>
);

const Method: React.FC<{ tag: string; color: string; title: string; children: React.ReactNode }> = ({
  tag,
  color,
  title,
  children,
}) => (
  <div className="flex items-start space-x-4">
    <div className={`w-12 h-12 ${color} rounded-full flex items-center justify-center flex-shrink-0 mt-1`}>
      <span className="text-white text-[10px] font-bold">{tag}</span>
    </div>
    <div>
      <h4 className="font-poppins font-semibold text-dark-gray mb-2">{title}</h4>
      <p className="text-gray-600">{children}</p>
    </div>
  </div>
);

export default Sentinel;
